import { TFM } from 'app/scripts/lib/markdown/Tfm';
import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { featureFlagClient } from '@trello/feature-flag-client';

interface MarkdownInfoType {
  rule: string;
}

interface MarkdownTypeCountType {
  [key: string]: number;
}

type AnalyzeFnType = (text: string) => [MarkdownInfoType];

interface MarkdownTFMAnalyzeMapType {
  description: { analyze: AnalyzeFnType };
  checkItems: { analyze: AnalyzeFnType };
  comments: { analyze: AnalyzeFnType };
}

export type MarkdownLocationType = keyof MarkdownTFMAnalyzeMapType;

// eslint-disable-next-line @trello/enforce-variable-case
const markdownTFMAnalyzeMap: MarkdownTFMAnalyzeMapType = {
  description: TFM.description,
  checkItems: TFM.checkItems,
  comments: TFM.comments,
};

export const analyzeMarkdown = (
  inputValue: string,
  type: MarkdownLocationType,
): MarkdownTypeCountType | null => {
  if (!inputValue) {
    return null;
  }
  if (!featureFlagClient.get('aaaa.web.trello-markdown-tracking', false)) {
    return null;
  }
  try {
    if (markdownTFMAnalyzeMap[type]) {
      // eslint-disable-next-line @trello/enforce-variable-case
      const markdownTFMAnalyzeFn = markdownTFMAnalyzeMap[type];
      const markdownData: MarkdownInfoType[] =
        markdownTFMAnalyzeFn.analyze(inputValue ?? '') || [];

      const markdownTypeCount: MarkdownTypeCountType = {};
      const filteredMarkdownData = markdownData.filter((markdownInfo) => {
        const { rule } = markdownInfo;
        return rule !== 'paragraph' && rule !== 'text';
      });

      if (filteredMarkdownData.length > 0) {
        filteredMarkdownData.forEach((markdownInfo) => {
          const { rule } = markdownInfo;

          if (!Object.hasOwnProperty.call(markdownTypeCount, rule)) {
            markdownTypeCount[rule] = 0;
          }

          markdownTypeCount[rule] = markdownTypeCount[rule] + 1;
        });

        return markdownTypeCount;
      }
    }
  } catch (err) {
    // @ts-expect-error
    sendErrorEvent(err, {
      tags: {
        ownershipArea: 'trello-aaaa',
        feature: 'Markdown Tracker',
      },
    });
  }
  return null;
};

export const markdownTracker = (
  inputValue: string,
  type: MarkdownLocationType,
) => {
  if (!inputValue) {
    return;
  }
  if (!featureFlagClient.get('aaaa.web.trello-markdown-tracking', false)) {
    return;
  }

  const start = Date.now();
  const markdownTypeCount = analyzeMarkdown(inputValue, type);

  if (markdownTypeCount) {
    Analytics.sendOperationalEvent({
      action: 'updated',
      actionSubject: 'trackMarkdown',
      source: 'lib:markdownTracker',
      attributes: {
        markdownTypeCount,
        markdownLocation: type,
      },
    });
  }

  const end = Date.now();

  Analytics.sendOperationalEvent({
    action: 'completed',
    actionSubject: 'trackMarkdown',
    source: 'lib:markdownTracker',
    attributes: {
      totalProcessTimeInMs: end - start,
    },
  });
};
